import React, { useState, useEffect } from 'react';
import { Menu, MenuItem } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import WidgetWrapper from './WidgetWrapper';
import tagService from '../services/tagService';

const ButtonMenu = ({
  options = [], // Array of {label, value} objects
  tagId = null,
  tagValue = null,
  value = null, // Alternative prop name (from tagBinding.property)
  isEditMode = false,
  // Color mapping based on tag value
  colorMap = {}, // e.g., { '1': '#00ff00', '0': '#ffffff' }
  defaultColor = '#ffffff',
  textColor = '#000000',
  // Style properties
  backgroundColor,
  foreColor,
  borderWidth,
  borderColor,
  borderRadius,
  fontSize,
  fontWeight,
  fontFamily,
  opacity,
  textAlign = 'center',
  companyId,
  onContextMenu, // Pass through context menu handler
  ...props
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  // Use tagValue or value prop (tagBinding.property may be 'value')
  const effectiveTagValue = tagValue ?? value;
  const [currentValue, setCurrentValue] = useState(effectiveTagValue);
  const open = Boolean(anchorEl);

  // Debug: Log props on mount and when tagValue changes
  useEffect(() => {
    console.log('[ButtonMenu] Props received:', {
      tagId,
      tagValue,
      value,
      effectiveTagValue,
      currentValue,
      options: options?.length || 0,
      optionsData: options,
      companyId,
      isEditMode
    });
  }, [tagId, tagValue, value, effectiveTagValue, options, companyId, isEditMode, currentValue]);

  useEffect(() => {
    console.log('[ButtonMenu] effectiveTagValue changed from', currentValue, 'to', effectiveTagValue);
    setCurrentValue(effectiveTagValue);
  }, [effectiveTagValue]);

  // Normalize value for comparison - handles "5.00 " vs "5" mismatch
  const normalizeValue = (val) => {
    if (val === null || val === undefined) return null;
    const str = String(val).trim();
    // Try to parse as number and get integer value
    const num = parseFloat(str);
    if (!isNaN(num)) {
      // Return integer string if it's a whole number
      return Number.isInteger(num) ? String(Math.round(num)) : str;
    }
    return str;
  };

  // Get button color based on tag value
  const getButtonColor = () => {
    let effectiveValue = currentValue;
    if ((effectiveValue === null || effectiveValue === undefined) && options && options.length > 0) {
      effectiveValue = options[0].value;
    }

    if (effectiveValue !== null && effectiveValue !== undefined) {
      const normalizedCurrent = normalizeValue(effectiveValue);

      // 1. colorMap'te varsa onu kullan (kullanıcının belirlediği renk)
      if (colorMap && colorMap[normalizedCurrent]) {
        return colorMap[normalizedCurrent];
      }

      // 2. Option'da custom renk varsa (default #ffffff değilse) kullan
      const matchingOption = options.find(opt => normalizeValue(opt.value) === normalizedCurrent);
      if (matchingOption?.color && matchingOption.color !== '#ffffff') {
        return matchingOption.color;
      }

      // 3. Fallback: değer bazlı varsayılan renkler
      const valueStr = String(effectiveValue).toUpperCase().trim();
      const numericValue = parseFloat(effectiveValue);

      if (valueStr === 'OFF' || valueStr === '0' || numericValue === 0 || valueStr === 'NORMAL') {
        return '#ffffff';
      }
      if (valueStr === 'ALARM' || valueStr === 'ARIZA') {
        return '#ff2400';
      }

      // Default: ON, 1, AUTO vb. → yeşil
      return '#008000';
    }

    return backgroundColor || defaultColor;
  };

  // Get button text (label of current value)
  const getButtonText = () => {
    if (currentValue !== null && currentValue !== undefined) {
      const normalizedCurrent = normalizeValue(currentValue);
      const option = options.find(opt => normalizeValue(opt.value) === normalizedCurrent);
      if (option) {
        return option.label;
      }
      // Fallback: show the raw value
      return normalizedCurrent || String(currentValue);
    }
    return options[0]?.label || 'Select';
  };

  const handleClick = (event) => {
    if (isEditMode) return;
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = async (option) => {
    handleClose();

    if (isEditMode) return;

    console.log('[ButtonMenu] Selected option:', option, 'for tagId:', tagId);
    setCurrentValue(option.value);

    // Update tag if connected
    if (tagId) {
      try {
        console.log('[ButtonMenu] Calling tagService.updateTagValue with:', { tagId, value: option.value, companyId });
        await tagService.updateTagValue(tagId, option.value, companyId);
        console.log('[ButtonMenu] Tag update successful');
      } catch (error) {
        console.error('[ButtonMenu] Failed to update tag value:', error);
        // Revert on error
        setCurrentValue(tagValue);
      }
    } else {
      console.warn('[ButtonMenu] No tagId provided, cannot update tag');
    }
  };

  const buttonColor = getButtonColor();
  const buttonText = getButtonText();

  // Determine text color based on background color
  // White background -> black text, colored background -> white text
  const getTextColor = () => {
    if (buttonColor === '#ffffff' || buttonColor === 'white') {
      return '#000000';
    }
    return '#ffffff';
  };

  const dynamicTextColor = getTextColor();

  return (
    <WidgetWrapper
      backgroundColor={buttonColor}
      foreColor={foreColor}
      borderRadius={borderRadius || 4}
      fontSize={fontSize}
      fontWeight={fontWeight}
      fontFamily={fontFamily}
      opacity={opacity}
      justifyContent="center"
      alignItems="center"
      padding="2px 4px"
      borderWidth={borderWidth || 1}
      borderColor={borderColor || '#000'}
      {...props}
    >
      <div
        onClick={handleClick}
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center',
          cursor: isEditMode ? 'default' : 'pointer',
          color: dynamicTextColor,
          fontSize: fontSize ? `${fontSize}px` : '10px',
          fontWeight: 'inherit',
          fontFamily: 'inherit',
          overflow: 'hidden'
        }}
      >
        <span
          style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            textAlign: textAlign
          }}
        >
          {buttonText}
        </span>
        <KeyboardArrowDown
          sx={{
            fontSize: '0.8em',
            color: dynamicTextColor,
            marginLeft: '2px',
            flexShrink: 0
          }}
        />
      </div>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        MenuListProps={{
          sx: {
            padding: 0
          }
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
      >
        {options.map((option, index) => (
          <MenuItem
            key={index}
            onClick={() => handleSelect(option)}
            sx={{
              fontSize: fontSize ? `${fontSize}px` : '12px',
              fontWeight: fontWeight || 'normal',
              fontFamily: fontFamily || 'inherit',
              minHeight: '28px',
              py: 0.5
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </WidgetWrapper>
  );
};

export default ButtonMenu;
